import React, { useCallback, useMemo, useRef, useState } from "react";
import { useStaticQuery, graphql, withPrefix } from "gatsby";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import classNames from "classnames";
import { Link } from "./link";
import ReevooBadge from "./reevoo-badge";
import ProductTag from "./elements/product-tag";
import BannerPushNotification from "./elements/banner-push-notification";
import BannerNotification from "./banner-notification";
import RepairabilityIndex from "./repairability-index";
import useEnergyClassImg from "../hooks/use-energy-class-img";
import safeSlufigy from "../utility/safe-slugify";
import { getResultTags } from "../utility/discount-tag";
import useEnergyLabelLink from "../utility/energy-label-link";
import { productClick } from "../utility/datalayer";
import placeholderImg from "../images/placeholder.png";
import ProdottiActions from "./elements/prodotti-actions";
import useNoRtl from "../hooks/use-no-rtl";
import AccessoriesAndConsumablesModal from "./accessories-and-consumables-modal";
import babelConfig from "../../babel-config.json";
import sortPosition from "../utility/sort-position";

const CardProdotto = ({
  data,
  className,
  productIds,
  toggleProduct,
  removeProduct,
  addCart,
  details,
  heading2,
  showDetails,
  config,
  origin,
  index,
  labelManagerLabels,
  compare,
  isList,
  isSingleProdInList,
  location,
  productNote,
  compareProducts,
  madeProductLineId,
  isCompare,
  isSliderProduct,
  onHide,
}) => {
  const { compatibleLines, compatiblePageLines } = data;

  const link = `/${[
    data.pageLine.slug.startsWith("/") ? data.pageLine.slug.substring(1) : data.pageLine.slug,
    data.key_prod,
    data.productName.toLowerCase(),
  ]
    .map(safeSlufigy)
    .join("/")}/`;
  const pathPrefix = withPrefix("/");
  const image = data.primaryimage.find((item) => item.imageType === "640");
  const productFamily = data.familiesIds && data.familiesIds.length ? data.familiesIds[0] : null;
  const pageName = data.pageLine.pageName ? data.pageLine.pageName[0] : "";
  const invertProductFamily =
    config?.invertProductFamily?.[0] === "yes" ||
    babelConfig.invertProductFamilyPaths.includes(pathPrefix);
  const productTitle =
    data?.lineId === 333 && !(data?.familiesIds?.length > 0) && data.productTitleNoFamilyAC
      ? data.productTitleNoFamilyAC
      : invertProductFamily
      ? [productFamily ? productFamily.title : "", pageName].filter(Boolean).join(" ")
      : [pageName, productFamily ? productFamily.title : ""].filter(Boolean).join(" ");

  const keySellingPoints = useMemo(() => {
    const familyTriples = productFamily && productFamily.triples;
    if (!data.triples && !familyTriples) return null;
    const concatTriples = familyTriples ? familyTriples.concat(data.triples) : data.triples;
    const triplePoints = concatTriples
      .filter((item) => !!item.keySellingPoint)
      .map((item) => item.keySellingPoint);
    // Max 5
    return (triplePoints.length ? triplePoints : data.minicard1 || []).slice(0, 5);
  }, [productFamily, data.triples, data.minicard1]);
  const attributes = useMemo(() => {
    if (details === "all")
      return data.attributes?.filter((attribute) => attribute.category.id !== 1068);
    return details
      ? data.attributes
          ?.filter((attribute) => attribute.category.id !== 1068)
          ?.filter((attr) => details.includes(attr.id))
      : null;
  }, [details, data.attributes]);

  const {
    site: { siteMetadata },
    currency,
  } = useStaticQuery(
    graphql`
      {
        site {
          siteMetadata {
            priceLanguage
          }
        }
        currency: liferayGeneralConfiguration {
          currency
        }
      }
    `
  );
  const noRtl = useNoRtl();

  const enabledForLine = [undefined, "[]"].includes(
    data.pageLine?.lineConfigurationFieldSet?.isNotEcommerce?.[0]
  );

  const productTags = useMemo(() => {
    return !noRtl
      ? getResultTags(data.campaignsCard, data.productTags, config, data, enabledForLine, 2)
      : getResultTags(data.campaignsCard, [], config, data, enabledForLine, 2);
  }, [data, config, noRtl, enabledForLine]);

  const energyClassImg = useEnergyClassImg(data.energyClass, data.lineId, config);

  const LANG = useMemo(
    () => siteMetadata.priceLanguage.split("-")[0].toUpperCase(),
    [siteMetadata.priceLanguage]
  );

  const energyClassLink = useEnergyLabelLink(data, LANG, config);

  const minicard4Text = useMemo(() => {
    if (!data.minicard4?.length) return null;
    const pageLineNames = data.pageLine?.pageName;
    const lineProductName = pageLineNames && pageLineNames[1] ? pageLineNames[1] : "";
    const miniCardText = data.minicard4.slice(0, 5).join(", ");
    return lineProductName ? lineProductName + " " + miniCardText : miniCardText;
  }, [data.minicard4, data.pageLine]);

  const clickDetail = useCallback(
    () => productClick(data, null, origin, index, currency),
    [data, origin, index, currency]
  );

  const [accessoriesAndConsumablesState, setAccessoriesAndConsumablesState] = useState(false);
  const closeModal = () => {
    setAccessoriesAndConsumablesState(false);
  };

  const {
    msrpPrice,
    sellingPrice,
    omnibusPrice,
    availableForSale,
    special_from_date,
    special_to_date,
    contributoRaee,
  } = data;
  const priceData = sellingPrice
    ? {
        msrpPrice,
        sellingPrice,
        omnibusPrice,
        availableForSale,
        special_from_date,
        special_to_date,
        contributoRaee,
      }
    : null;

  const accessoriesAndConsumablesCheck =
    madeProductLineId !== 333 && data.lineId === 333 && origin === "Related product - PDP";
  const pageLineNames = data?.pageLine.pageName;
  const lineProductName2 =
    pageLineNames && pageLineNames[1] && pageLineNames[1] ? pageLineNames[1] : "";

  const readMoreDescription =
    data?.productDescAfter || data?.minicard4.length > (lineProductName2 ? 7 : 8);

  const refWtb = useRef();
  const onClickWtb = () => refWtb.current?.click?.();

  const noRetailerPageCountryMap = new Map([
    ["/it_IT/", "haier.it"],
    [("/fr_FR/", "haier.fr")],
    [("/de_DE/", "haier.de")],
    [("/en_GB/", "haier.uk")],
    [("/pl_PL/", "haier.pl")],
    [("/nl_BE/", "haier.be")],
    [("/fr_BE/", "haier.be")],
    [("/nl_NL/", "haier.nl")],
    [("/bg_BG/", "haier.bg")],
    [("/fr_CH/", "haier.ch")],
    [("/de_CH/", "haier.ch")],
    [("/it_CH/", "haier.ch")],
    [("/es_ES/", "haier.es")],
    [("/el_GR/", "haier.gr")],
    [("/ru_RU/", "haier.ru")],
    [("/cs_CZ/", "haier.cz")],
    [("/sk_SK/", "haier.sk")],
    [("/tr_TR/", "haier.tr")],
    [("/pt_PT/", "haier.pt")],
    [("/sl_SI/", "haier.sl")],
    [("/hr_HR/", "haier.hr")],
    [("/en_IE/", "haier.ie")],
    [("/ro_RO/", "haier.ro")],
    [("/rs_SR_latin/", "haier.rs")],
    [("/hu_HU/", "haier.hu")],
    [("/uk_UA/", "haier.ua")],
    [("/no_NO/", "haier.no")],
    [("/da_DK/", "haier.dk")],
    [("/fi_FI/", "haier.fi")],
    [("/sv_SE/", "haier.se")],
    [("/lv_LV/", "haier.lv")],
    [("/lt_LT/", "haier.lt")],
    [("/et_EE/", "haier.ee")],
    [("/fr_MA/", "haier.ma")],
  ]);

  const noRetailerPage = data?.pageLine?.lineConfigurationFieldSet?.noRetailerPage?.[0] || "";
  const checkLocatorCountry = (pathPrefix) => {
    if (pathPrefix === "/nl_BE/" || pathPrefix === "/nl_NL/") {
      return "nl";
    }
    if (pathPrefix === "/fr_BE/" || pathPrefix === "/fr_FR/" || pathPrefix === "/fr_CH/") {
      return "fr";
    }
    if (pathPrefix === "/de_DE/" || pathPrefix === "/de_CH/") {
      return "de";
    }
    if (pathPrefix === "/es_ES/") {
      return "es";
    }
    if (pathPrefix === "/it_IT/" || pathPrefix === "/it_CH/") {
      return "it";
    }
    if (pathPrefix === "/pl_PL/") {
      return "pl";
    }
    if (pathPrefix === "/pt_PT/") {
      return "pt";
    } else {
      return "en";
    }
  };

  const storeLocatorPageAC = `https://www.haierspares.eu/${checkLocatorCountry(pathPrefix)}/${
    data.key_prod
  }/?utm_source=${noRetailerPageCountryMap.get(
    pathPrefix
  )}&utm_medium=referral&utm_campaign=always_on_brand_website`;
  const storeLocatorPageCP = `https://www.premiumservicesforcareplusprotect.eu/${checkLocatorCountry(
    pathPrefix
  )}/${data.key_prod}/?utm_source=${noRetailerPageCountryMap.get(
    pathPrefix
  )}&utm_medium=referral&utm_campaign=always_on_brand_website`;

  const storeLink =
    noRetailerPage === "no retailer" && data?.brandName === "Haier"
      ? storeLocatorPageAC
      : noRetailerPage === "no retailer" && data?.brandName === "Care-Protect"
      ? storeLocatorPageCP
      : null;

  const compatibleLinesTitle = !!compatiblePageLines?.length
    ? compatiblePageLines
        ?.filter((line) => line?.pageLine?.title)
        .map((line) => {
          return {
            title: line?.pageLine?.title,
          };
        })
        .concat(
          compatibleLines?.reduce((result, line) => {
            const isDuplicate = compatiblePageLines
              ?.filter((line) => line?.pageLine?.title)
              ?.some((item) => `${item?.pageLine?.lineId}` === `${line?.id}`);
            if (!isDuplicate) {
              result.push(line);
            }
            return result;
          }, [])
        )
        ?.map((line) => line?.title)
        .join(", ")
    : compatibleLines?.map((line) => line?.title).join(", ");

  return (
    <div className={`prodotti__card${className ? ` ${className}` : ""} d-flex flex-column`}>
      <div className="text-center">
        <BannerPushNotification sleekNoteId="sn-785b13d4-ee9e-4fc6-acfe-e7bcc50e61dd">
          {!!productTags?.length && !noRtl ? (
            <div className="prodotti__tags">
              {productTags.sort(sortPosition).map((tag, i) => (
                <ProductTag
                  className={!!isCompare && i > 0 ? "d-none d-md-block" : ""}
                  data={tag}
                  location={location}
                  key={tag.id}
                  contributoRaee={data?.contributoRaee}
                />
              ))}
            </div>
          ) : (
            <div className="py-3 mb-2"></div>
          )}
        </BannerPushNotification>
      </div>
      {removeProduct ? (
        <button
          className="prodotti__remove"
          name={data.key_prod}
          onClick={removeProduct}
          aria-label="Remove"
        >
          <span className="icon-close"></span>
        </button>
      ) : null}
      <div className="prodotti__img-wrapper mb-3">
        <div className="prodotti__square">
          <Link
            className="prodotti__img-link d-flex justify-content-center "
            to={accessoriesAndConsumablesCheck ? null : link}
            onClick={
              accessoriesAndConsumablesCheck
                ? () => {
                    setAccessoriesAndConsumablesState(true);
                    clickDetail();
                  }
                : () => {
                    onHide();
                    clickDetail();
                  }
            }
            state={{ origin }}
          >
            {image?.gatsbyImageData ? (
              <GatsbyImage
                image={getImage(image)}
                alt={data.productName}
                className="prodotti__img"
                width="312"
                height="312"
              />
            ) : (
              <img
                className="prodotti__img"
                src={image ? image.imageUrl : placeholderImg}
                alt={data.productName}
                width="312"
                height="312"
                loading="lazy"
              />
            )}
            {!!data.bannerNotification && !!data.bannerNotification.length && (
              <div className="prodotti__img--notification">
                <BannerNotification data={data.bannerNotification[0]}></BannerNotification>
              </div>
            )}
          </Link>
          {(!!config?.isReevooActiveFieldSet?.isReevooActive?.length ||
            !!config?.isReevooActiveFieldSet?.isBazaarVoiceActive?.length) && (
            <div
              className={classNames(
                "prodotti__rating d-flex align-items-center",
                toggleProduct && productIds && !isSingleProdInList && "prodotti__rating--compare"
              )}
            >
              <ReevooBadge
                productId={data.key_prod}
                productUrl={link}
                isSliderProduct={isSliderProduct}
              />
            </div>
          )}
          {toggleProduct && productIds && !isSingleProdInList && data?.lineId !== 333 && (
            <label className="product-detail__compare-label product-detail__compare-label--pdp">
              <input
                aria-label={
                  productIds.includes(data.key_prod)
                    ? labelManagerLabels.removeFromComparison
                    : labelManagerLabels.compare
                }
                type="checkbox"
                checked={productIds.includes(data.key_prod)}
                onChange={toggleProduct}
                name={data.key_prod}
              />
              <span className="product-detail__compare-labelText">
                {labelManagerLabels.compare}
              </span>
            </label>
          )}
        </div>
      </div>
      <div
        className={classNames(
          "prodotti__data mb-3 flex-fill",
          isSingleProdInList ? "mt-3" : "mt-5"
        )}
      >
        <div className="prodotti__code mt-2">
          <div className="d-flex flex-wrap align-items-center justify-content-between">
            <Link
              to={accessoriesAndConsumablesCheck ? null : link}
              onClick={
                accessoriesAndConsumablesCheck
                  ? () => {
                      setAccessoriesAndConsumablesState(true);
                      clickDetail();
                    }
                  : () => {
                      onHide();
                      clickDetail();
                    }
              }
              className="prodotti__link text-xs-semibold"
              state={{ origin }}
            >
              {data.productCode}
            </Link>
            <div className="prodotti__doc">
              {data.energyClass &&
                data.energyClass !== "-" &&
                (energyClassLink ? (
                  <a
                    href={energyClassLink}
                    target="_blank"
                    rel="noreferrer"
                    className="prodotti__doc-energy-class"
                  >
                    <img
                      src={energyClassImg}
                      className={classNames(
                        "icon-energy-rating",
                        compare ? "ml-1 ml-sm-2 pl-sm-1" : "ml-2 pl-1"
                      )}
                      height="30"
                      alt={data.energyClass}
                    />
                  </a>
                ) : (
                  <img
                    src={energyClassImg}
                    className={classNames(
                      "icon-energy-rating",
                      compare ? "ml-1 ml-sm-2 pl-sm-1" : "ml-2 pl-1"
                    )}
                    height="30"
                    alt={data.energyClass}
                  />
                ))}
              {!!data.repairabilityIndex[0] && (
                <RepairabilityIndex
                  data={data.repairabilityIndex[0]?.product?.find(
                    (index) => index.sku === data.key_prod
                  )}
                  lineId={data.lineId}
                />
              )}
            </div>
          </div>
        </div>
        {heading2 ? (
          <h2 className="prodotti__title heading-xs-bold mb-0">
            <Link
              to={accessoriesAndConsumablesCheck ? null : link}
              onClick={
                accessoriesAndConsumablesCheck
                  ? () => {
                      setAccessoriesAndConsumablesState(true);
                      clickDetail();
                    }
                  : () => {
                      onHide();
                      clickDetail();
                    }
              }
              className="prodotti__link"
              state={{ origin }}
            >
              <span dangerouslySetInnerHTML={{ __html: productTitle }}></span>
            </Link>
          </h2>
        ) : (
          <h3 className="prodotti__title heading-xs-bold mb-0">
            <Link
              to={accessoriesAndConsumablesCheck ? null : link}
              onClick={
                accessoriesAndConsumablesCheck
                  ? () => {
                      setAccessoriesAndConsumablesState(true);
                      clickDetail();
                    }
                  : () => {
                      onHide();
                      clickDetail();
                    }
              }
              className="prodotti__link"
              state={{ origin }}
            >
              <span dangerouslySetInnerHTML={{ __html: productTitle }}></span>
            </Link>
          </h3>
        )}
        {(minicard4Text || data.productDescBefore) && (
          <Link
            to={accessoriesAndConsumablesCheck ? null : link}
            onClick={
              accessoriesAndConsumablesCheck
                ? () => {
                    setAccessoriesAndConsumablesState(true);
                    clickDetail();
                  }
                : () => {
                    onHide();
                    clickDetail();
                  }
            }
            className="prodotti__link"
            state={{ origin }}
          >
            <div>
              <p className="prodotti__desc text-xs-medium">
                {data.productDescBefore ? data.productDescBefore : minicard4Text}
              </p>
            </div>
          </Link>
        )}
        {keySellingPoints && !!keySellingPoints.length && (
          <Link
            to={accessoriesAndConsumablesCheck ? null : link}
            onClick={
              accessoriesAndConsumablesCheck
                ? () => {
                    setAccessoriesAndConsumablesState(true);
                    clickDetail();
                  }
                : () => {
                    onHide();
                    clickDetail();
                  }
            }
            className="prodotti__link"
            state={{ origin }}
          >
            <ul className="prodotti__info text-xs-light">
              {keySellingPoints.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </Link>
        )}
      </div>
      <ProdottiActions
        data={data}
        addCart={addCart}
        wtbAndRetailersActive={config.wtbAndRetailersActive}
        origin={origin}
        link={link}
        labelManagerLabels={labelManagerLabels}
        location={location}
        compare={compare}
        show={!!(toggleProduct && productIds)}
        isList={isList}
        clickDetail={clickDetail}
        storeLink={storeLink}
      />
      {attributes && (
        <div className={`flex-column prodotti__details ${showDetails ? "d-flex" : "d-md-none"}`}>
          {attributes.map((attr) => (
            <div
              className="prodotti__value py-1 py-lg-0 d-flex flex-column flex-md-row justify-content-center"
              key={attr.id}
            >
              <div className="mb-2 d-md-none">{attr.fieldName}</div>
              <span className="d-block">
                {!attr.fieldValue && attr.fieldValue !== false ? (
                  <span className="icon-close icon-close--red"></span>
                ) : typeof attr.fieldBoolean === "boolean" ? (
                  attr.fieldBoolean ? (
                    <span className="icon-tick icon-tick--green"></span>
                  ) : (
                    <span className="icon-close icon-close--red"></span>
                  )
                ) : (
                  <span>{attr.fieldValue}</span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
      {accessoriesAndConsumablesState && (
        <AccessoriesAndConsumablesModal
          show={accessoriesAndConsumablesState}
          onHide={closeModal}
          babelProdotto={data}
          energyClassImg={energyClassImg}
          config={config}
          productTitle={productTitle}
          lineProductName2={lineProductName2}
          readMoreDescription={readMoreDescription}
          location={location}
          labelManagerLabels={labelManagerLabels}
          siteMetadata={siteMetadata}
          liferayProductNotes={productNote}
          priceData={priceData}
          compareProducts={compareProducts}
          storeLink={storeLink}
          onClickWtb={onClickWtb}
          compatibleLinesTitle={compatibleLinesTitle}
        />
      )}
    </div>
  );
};

export default CardProdotto;

export const fragment = graphql`
  fragment BabelProdottoFragment on BabelProdotto {
    key_prod
    badgePdp {
      ...ProductBadgePDPFragment
    }
    campaignBadge: campaigns(positions: ["PDP | Badge"]) {
      filterField {
        pmInStock
        pmSpecialPrice
      }
      date {
        pmEnd
        pmStart
      }
      boxAndPosition {
        pmBoxPosition
        webContent {
          ...ProductBadgePDPFragment
        }
      }
    }
    campaignsPDP: campaigns(positions: ["PDP | Banner Promo"]) {
      filterField {
        pmInStock
        pmSpecialPrice
      }
      date {
        pmEnd
        pmStart
      }
      boxAndPosition {
        pmBoxPosition
        webContent {
          ...BannerPromoCMFragment
        }
      }
    }
    primaryThumb: primaryimage {
      imageType
      gatsbyImageData(width: 85, formats: [AUTO, WEBP])
    }
    primaryMedium: primaryimage {
      imageType
      gatsbyImageData(width: 640)
    }
    primaryFull: primaryimage {
      imageType
      gatsbyImageData(layout: FULL_WIDTH)
    }
    s3Thumb: s3Images {
      imageType
      gatsbyImageData(width: 85, formats: [AUTO, WEBP])
    }
    s3Medium: s3Images {
      imageType
      gatsbyImageData(width: 640)
    }
    s3Full: s3Images {
      imageType
      gatsbyImageData(layout: FULL_WIDTH)
    }
    otherThumb: otherImages {
      imageType
      imageUrl
      gatsbyImageData(width: 85, formats: [AUTO, WEBP])
    }
    otherMedium: otherImages {
      imageType
      imageUrl
      gatsbyImageData(width: 640)
    }
    otherFull: otherImages {
      imageType
      imageUrl
      gatsbyImageData(layout: FULL_WIDTH)
    }
    otherDesign: otherImages {
      imageType
      imageUrl
      gatsbyImageData(width: 100)
    }
    primaryimage {
      imageType
      imageUrl
      resize(width: 1200) {
        src
      }
    }
    s3Images {
      imageType
      imageUrl
    }
    otherImages {
      imageType
      imageUrl
    }
  }
  fragment BabelProdottoCardFragment on BabelProdotto {
    key_prod
    brandName
    minicard1
    minicard2
    aecException
    minicard4
    specifications
    productDescBefore
    productDescAfter
    productMsrpPrice
    productTitleNoFamilyAC
    productSellingPrice
    productOmnibusPrice
    preorderActive
    shopifyServices
    shopLink
    assetId
    energyLabelCav
    hasDirectProducts
    aecException
    compatiblePageLines {
      pageLine {
        lineId
        liferayFields {
          title
        }
      }
    }
    compatibleLines {
      title
      id
    }
    manuals {
      filename
      lang
      type
      productGroup
    }
    repairabilityIndex {
      ...RepairabilityIndex
    }
    pageLine {
      liferayFields {
        taxonomyCategoryBriefs {
          name
        }
        title
      }
      slug
      pageName
      lineConfigurationFieldSet {
        isNewEnergyLabelEu
        isNewEnergyLabelUk
        isNotEcommerce
        noRetailerPage
      }
      mainfieldsFilters {
        ... on LiferayMainfieldsAndFilters {
          miniCard1
          miniCard4
        }
      }
    }
    productName
    productCode
    extraInfo {
      ca4_CHG_HAIER_SAP
    }
    energyClass
    eanCode
    lineId
    date_rt_ISO
    productPriority
    familiesIds {
      title
      triples {
        keySellingPoint
      }
    }
    triples {
      keySellingPoint
    }
    primaryimage {
      imageType
      imageUrl
      gatsbyImageData(width: 450)
    }
    attributesMap
    productTags {
      id
      productTag
      color
      colorHexa
      colorHexaText
      colorCategory
      position
      liferayFields {
        taxonomyCategoryBriefs {
          name
        }
      }
    }
    bannerNotification {
      ...BannerNotificationFragment
    }
    campaignsCard: campaigns(positions: ["CARD | Tag Prodotto"]) {
      date {
        pmStart
        pmEnd
      }
      filterField {
        pmInStock
        pmSpecialPrice
      }
      boxAndPosition {
        pmBoxPosition
        webContent {
          ... on LiferayProductTag {
            id
            productTag
            color
            colorHexa
            colorHexaText
            colorCategory
            position
            liferayFields {
              taxonomyCategoryBriefs {
                name
              }
            }
          }
        }
      }
      tagActive
    }
  }
  fragment BabelProdottoAttributesFragment on BabelProdotto {
    ...BabelProdottoCardFragment
    attributes {
      id
      fieldName
      fieldValue
      fieldBoolean
      category {
        id
      }
    }
  }
`;
